import { CSSProperties, useEffect, useState } from "react";
import { BrandContent } from "modules/Types";
import { pageView } from "analytics";
import Sty from "modules/Sty";
import WebsiteListItem from "./WebsiteListItem";
import {
    formattedWebsiteUrl,
    generateShortUniqueId,
    getSearchParam,
    getStatusToShowPaymentFailedBanner,
    makeKey,
    slugToTitle,
    styx
} from 'modules/Utils';
import { CreateWebsiteOptions, CreateNewWebsiteSteps } from "./CreateNewWebsiteModal";
import { WEBSITE_CREATION_TYPE } from "modules/Types";
import { OverallWebsiteDashboard } from "./OverallWebsiteDashboard/OverallWebsiteDashboard";
import useCurrentBrand, { BrandExistsState } from 'Hooks/useCurrentBrand';
import useOrganization from 'Hooks/useOrganization';
import OrganizationSubscriptionStatusBanner from "components/shareable/OrganizationSubscriptionStatusBanner";
import Colors from "modules/Colors";
import PageSymphony from "elements/PageSymphony";
import DefaultAdditionalActions from "components/shareable/DefaultAdditionalActions";
import Loader from "elements/Loader";
import TabsSymphony, { TabItem } from "elements/TabsSymphony";
import TabSymphony from "elements/TabSymphony";
import CardSymphony from "elements/CardSymphony";
import useWebsiteBuilder, { getWebsiteBuilderPath } from "Hooks/useWebsiteBuilder";
import WebsiteBuilderMiniature from "components/shareable/WebsiteBuilderMiniature";
import Icon from "elements/Icon";
import dayjs from "dayjs";
import TagSymphony from "elements/TagSymphony";
import ButtonSymphony from "elements/ButtonSymphony";
import { useHistory } from "react-router-dom";
import { SYMPHONY_WEBSITE_URL, WebsiteTemplateCategory } from "modules/Const";
import { WebsiteType } from "modules/Const";
import DebugBox from "components/shareable/DebugBox";
import useScreen from "Hooks/useScreen";
import { toast } from "react-toastify";
import WebsiteGallery from "components/WebsiteGallery";
import CreateNewWebsiteModalV2 from "components/shareable/CreateNewWebsiteModalV2";

const isDev = process.env.NODE_ENV === 'development';

enum WebsitePageTabs {
    SITES = 'sites',
    DASHBOARD = 'dashboard',
    GALLERY = 'gallery',
}

const WEBSITE_TABS = [
    {
        id: WebsitePageTabs.SITES,
        title: "Websites"
    },
    {
        id: WebsitePageTabs.DASHBOARD,
        title: "Dashboard"
    },
    {
        id: WebsitePageTabs.GALLERY,
        title: "Gallery"
    }
]

const WebsitePage = () => {
    const history = useHistory();
    const {
        mStyle,
        mobileView
    } = useScreen();
    const {
        currentBrand,
        isBrandAdmin,
        websites,
        isLoadingWebsites,
        getWebsites,
        isWebsitesInitialized
    } = useCurrentBrand() as BrandExistsState;

    const {
        websites: websiteBuilderWebsites,
        getWebsites: getWebsiteBuilderWebsites,
        setInitialWebsiteType,
        setShowCreateNewWebsiteModal,
        setShowCreateNewWebsiteModalV2,
        isLoadingWebsiteBuilder,
        isLoadingWebsitesGallery,
        isWebsitesInitialized: isWebsitesInitializedV2,
        setSelectedTemplateCategory,
    } = useWebsiteBuilder();

    const websiteSlug = generateShortUniqueId()
    const websiteBuilderPath = getWebsiteBuilderPath(currentBrand.slug, websiteSlug)

    const websiteTypeParam = getSearchParam({ name: 'websiteType' });


    const [step, setStep] = useState<CreateNewWebsiteSteps>(CreateNewWebsiteSteps.SELECT_OPTION)

    const showPaymentFailedBanner = getStatusToShowPaymentFailedBanner(currentBrand)
    const { remainingFreeTrial, isOrganizationInactive } = useOrganization()

    const [selectedWebsiteTab, setSelectedWebsiteTab] = useState<TabItem>(WEBSITE_TABS[0]);

    const [actionedItem, setActionedItem] = useState<BrandContent | undefined>(undefined)

    const openCreateNewWebsiteModal = (websiteKey?: string) => {
        setSelectedTemplateCategory(websiteKey as WebsiteTemplateCategory || null);
        setInitialWebsiteType(websiteKey as WebsiteType || null);
        setStep(CreateNewWebsiteSteps.SELECT_OPTION)
        // setShowCreateNewWebsiteModal(true);
        setShowCreateNewWebsiteModalV2(true);
    }

    useEffect(() => {
        if (websiteTypeParam) {
            openCreateNewWebsiteModal(websiteTypeParam);
        }

        const brandId = currentBrand?.id;
        getWebsiteBuilderWebsites({ brandId });
        getWebsites({ brandId });
    }, []);

    useEffect(() => {
        pageView("Website List")
    }, [currentBrand])

    useEffect(() => {
        if (selectedWebsiteTab.id === WebsitePageTabs.DASHBOARD) {
            pageView("Website Dashboard")
        }
    }, [selectedWebsiteTab.id])

    const renderWebsiteBuilderSites = () => {
        return websiteBuilderWebsites.sort((a, b) => {
            return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        }).map((website, index) => {
            const {
                slug,
                updatedAt,
                createdAt,
                websiteContent
            } = website;

            const websiteLink = `${SYMPHONY_WEBSITE_URL}/${currentBrand?.slug}/${slug}`;
            const websiteLinkProd = `https://symphony.to/${currentBrand?.slug}/${slug}`;
            return (
                <div
                    key={makeKey('websiteBuilderSite', index)}
                    className="websiteBuilderSiteRow"
                    style={mStyle(sty, 'websiteBuilderSiteRow')}
                >
                    <WebsiteBuilderMiniature
                        className="websiteHomepageMiniature"
                        style={{
                            cursor: 'pointer',
                        }}
                        website={website}
                        onClick={() => {
                            history.push(`/website-builder/${currentBrand?.slug}/${slug}`)
                        }}
                    />

                    <div className="websiteBuilderSiteInfoContainer" style={mStyle(sty, 'websiteBuilderSiteInfoContainer')}>
                        <div className="websiteBuilderSiteInfo" style={sty.websiteBuilderSiteInfo}>
                            <div className="websiteBuilderSiteTitle">
                                {slugToTitle(slug)}
                            </div>
                            <div className="websiteBuilderSiteTags" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                {/* <TagSymphony
                                className="websiteBuilderSiteContentLength"
                                title={`${websiteContent.length} items`}
                                type="square"
                            /> */}
                                <TagSymphony
                                    className="websiteBuilderSiteStatus"
                                    title="Website Builder"
                                    type="square"
                                />
                            </div>
                            <div style={{ display: 'flex', gap: 4 }}>
                                <div style={Sty.text12grey}>
                                    Created {dayjs(createdAt).fromNow()} –
                                </div>
                                <div style={styx(Sty.text12grey, { fontStyle: 'italic' })}>
                                    (Updated {dayjs(updatedAt).fromNow()})
                                </div>
                            </div>
                            <div
                                className="websiteBuilderSiteLinkContainerOuter"
                                style={sty.websiteBuilderSiteLinkContainerOuter}
                            >
                                <div
                                    className="websiteBuilderSiteLinkContainer"
                                    style={sty.websiteBuilderSiteLinkContainer}
                                    onClick={() => {
                                        navigator.clipboard.writeText(websiteLink);
                                        toast.success('Link copied to clipboard');
                                    }}
                                >
                                    <div
                                        className="websiteBuilderSiteLink"
                                        style={{ color: Colors.purple, fontSize: 14 }}
                                    >
                                        {websiteLink}
                                    </div>
                                    <Icon
                                        className="websiteBuilderSiteLinkIcon"
                                        icon="link"
                                        size={16}
                                        color={Colors.greyDark}
                                    />
                                </div>
                                <Icon
                                    className="websiteBuilderSiteLinkIconExternal"
                                    icon="externalLink"
                                    size={22}
                                    onClick={() => {
                                        window.open(websiteLink, '_blank');
                                    }}
                                />
                                {isDev &&
                                    <Icon
                                        className="websiteBuilderSiteLinkIconExternal"
                                        icon="externalLink"
                                        size={22}
                                        color={Colors.green}
                                        onClick={() => {
                                            window.open(websiteLinkProd, '_blank');
                                        }}
                                    />
                                }
                            </div>
                        </div>

                        <div className="websiteBuilderSiteActions" style={sty.websiteBuilderSiteActions}>
                            <ButtonSymphony
                                className="websiteBuilderSiteEditButton"
                                variant="text"
                                onClick={() => {
                                    history.push(`/website-builder/${currentBrand?.slug}/${slug}`)
                                }}
                                size={mobileView ? "small" : undefined}
                            >
                                Edit Site
                            </ButtonSymphony>
                            <ButtonSymphony
                                className="websiteBuilderSiteEditButton"
                                variant="text"
                                onClick={() => {
                                    history.push(`/website-builder/${currentBrand?.slug}/${slug}?tab=analytics`)
                                }}
                                size={mobileView ? "small" : undefined}
                            >
                                View Analytics
                            </ButtonSymphony>
                            <ButtonSymphony
                                className="websiteBuilderSiteEditButton"
                                variant="text"
                                onClick={() => {
                                    navigator.clipboard.writeText(websiteLink);
                                }}
                                size={mobileView ? "small" : undefined}
                            >
                                Copy Link
                            </ButtonSymphony>
                        </div>
                    </div>
                </div>
            )
        })
    }

    const renderWebsiteItems = () => {
        if (websites?.length === 0 && !isBrandAdmin) {
            return (
                <div className="noWebsitesCreatedYet" style={Sty.text14}>
                    No websites created yet.
                </div>
            )
        }

        return websites?.map((item: BrandContent, index: number) => {
            const thumbnail = item.content_metadata.thumbnail_url || currentBrand?.image || ''
            const primaryTitle = item.name
            let websiteType: WebsiteType = WebsiteType.RECORD_STREAMS
            let additionalLabel = null

            let hasPresaveDetails = 'presave' in item.content_metadata
            // if this is a presave, but it hasnt been saved yet - mark it as a presave site
            if (hasPresaveDetails && !(item.content_metadata.presave?.saved_at)) {
                websiteType = WebsiteType.PRESAVE
            } else if (item.type === WebsiteType.DATA_COLLECTOR) {
                websiteType = WebsiteType.DATA_COLLECTOR
            } else if (item.type === WebsiteType.TOUR) {
                websiteType = WebsiteType.TOUR
            } else {
                switch (item.content_metadata.type) {
                    case 'single':
                    case 'record':
                        additionalLabel = 'Single'
                        break;
                    case 'album':
                    case 'project':
                        additionalLabel = 'Album/Project'
                        break;
                    case 'playlist':
                        additionalLabel = 'Playlist'
                        break;
                }
            }

            let mainStyle = {
                bgType: 'artwork',
                bgColor: Colors.textColor as string,
                primaryColor: Colors.purple as string,
                secondaryColor: Colors.textColor as string,
                buttonConfig: "boxes"
            }

            if (item.primary_customization) {
                mainStyle = {
                    ...mainStyle,
                    ...item.primary_customization
                }
            } else if (item.content_metadata.website?.style) {
                mainStyle = {
                    ...mainStyle,
                    ...item.content_metadata.website.style
                }
            } else if (hasPresaveDetails && item.content_metadata.presave?.style) {
                mainStyle = {
                    ...mainStyle,
                    ...('presave' in item.content_metadata && item.content_metadata.presave?.style)
                }
            }

            let creationType: WEBSITE_CREATION_TYPE = WEBSITE_CREATION_TYPE.AUTOMATIC
            if (item.content_metadata.publishReason === 'marketing_campaign') {
                creationType = WEBSITE_CREATION_TYPE.CAMPAIGN
            }
            else if (hasPresaveDetails) {
                creationType = WEBSITE_CREATION_TYPE.PRESAVE
            }

            const websiteUrl = formattedWebsiteUrl(currentBrand, item)

            let linkToEdit = `/website/${item.type}/${item.slug}`
            if (item.content_metadata.presave && !item.content_metadata.presave.saved_at) {
                const linkedPresaveCampaigns = item.linked_campaigns?.filter(campaign => campaign.campaign_metadata.campaign_type === 'pre_save')
                if (linkedPresaveCampaigns && linkedPresaveCampaigns?.length > 0) {
                    linkToEdit = `/marketing/campaigns/${linkedPresaveCampaigns[0].id}`
                }
            }

            return (
                <WebsiteListItem
                    key={makeKey('websiteListItem', item.id)}
                    content={item}
                    setActionedItem={setActionedItem}
                    titleText={primaryTitle}
                    thumbnailSrc={thumbnail}
                    subtitle={null}
                    websiteUrl={websiteUrl}
                    linkToEdit={linkToEdit}
                    onClick={() => { }}
                    creationType={creationType}
                    websiteType={websiteType}
                    websiteSubtypeLabel={additionalLabel}
                    createdAt={item.createdAt}
                    updatedAt={item.updatedAt}
                    websiteStyle={{
                        bgType: mainStyle?.bgType,
                        primaryColor: mainStyle?.primaryColor,
                        buttonConfig: mainStyle?.buttonConfig,
                        secondaryColor: mainStyle?.secondaryColor,
                        bgColor: mainStyle?.bgColor,
                    }}
                />
            )
        })
    }

    // NOTE: render()
    return (
        <>
            <PageSymphony
                className="websiteListComponent"
                isContent
            >
                {(showPaymentFailedBanner || remainingFreeTrial() > 0 || isOrganizationInactive()) &&
                    <OrganizationSubscriptionStatusBanner onlyShowFailedPayments />
                }

                <CardSymphony
                    className="cardTitleWebsiteList"
                    title="Website"
                    subtitle="Own your audience and grow with our customizable website builder."
                    additionalButtons={
                        <>
                            {isBrandAdmin &&
                                <DefaultAdditionalActions
                                    className="defaultAdditionalActionsWebsiteList"
                                    additionalItemsText="Popular Websites:"
                                    buttonText="Create New Website"
                                    // onButtonClick={() => openCreateNewWebsiteModal()}
                                    onButtonClick={() => {
                                        history.push(websiteBuilderPath)
                                        setShowCreateNewWebsiteModalV2(true)
                                    }}
                                    campaignItems={[
                                        {
                                            key: WebsiteTemplateCategory.MUSIC,
                                            title: "Promote Music",
                                            icon: 'musicSite',
                                            link: "#",
                                        },
                                        {
                                            key: WebsiteTemplateCategory.COLLECT_DATA,
                                            title: "Collect Fan Data",
                                            icon: 'dataCollectorSite',
                                            link: "#",
                                        },
                                        {
                                            key: WebsiteTemplateCategory.EVENTS,
                                            title: "Promote Events",
                                            icon: 'tourSite',
                                            link: "#",
                                        },
                                    ]}
                                    onCampaignItemClick={(websiteKey) => {
                                        openCreateNewWebsiteModal(websiteKey)
                                        history.push(`/website-builder/${currentBrand?.slug}/${websiteSlug}`)
                                    }}
                                />
                            }
                        </>
                    }
                />
                <CardSymphony
                    className="websiteListTabsContainer"
                >
                    <TabsSymphony
                        className="websiteListTabs"
                        selectedTab={selectedWebsiteTab}
                        onChange={(tab) => setSelectedWebsiteTab(tab as TabItem)}
                        tabs={WEBSITE_TABS}
                        isLoading={isLoadingWebsites || isLoadingWebsiteBuilder || isLoadingWebsitesGallery}
                        isBorderBottom
                    />

                    <TabSymphony
                        className="websiteListTabSites"
                        visible={selectedWebsiteTab.id === WebsitePageTabs.SITES}
                    >
                        {renderWebsiteBuilderSites()}
                        {renderWebsiteItems()}
                        {isWebsitesInitializedV2 && isWebsitesInitialized && websites?.length === 0 && websiteBuilderWebsites?.length === 0 &&
                            <CardSymphony
                                className="websiteListHeader"
                                title="Create your first site"
                                isNoPadding
                                size="small"
                            >
                                {/* <CreateWebsiteOptions step={step} setStep={setStep} /> */}
                                <CreateNewWebsiteModalV2
                                    className="createNewWebsiteModalV2FromWebsitePage"
                                    isModal={false}
                                    isRedirect
                                />
                            </CardSymphony>
                        }
                    </TabSymphony>

                    {selectedWebsiteTab.id === WebsitePageTabs.DASHBOARD &&
                        <TabSymphony
                            className="websiteListTabDashboard"
                            visible={selectedWebsiteTab.id === WebsitePageTabs.DASHBOARD}
                            style={{ gap: 24, paddingTop: 24 }}
                        >
                            <OverallWebsiteDashboard />
                        </TabSymphony>
                    }

                    {selectedWebsiteTab.id === WebsitePageTabs.GALLERY &&
                        <TabSymphony
                            className="websiteListTabGallery"
                            visible={selectedWebsiteTab.id === WebsitePageTabs.GALLERY}
                        >
                            <WebsiteGallery
                                className="websiteGalleryFromWebsitePage"
                                isButtonAbove
                            />
                        </TabSymphony>
                    }
                </CardSymphony>
            </PageSymphony>
        </>
    );
};

const sty: Record<string, CSSProperties> = {
    websiteBuilderSiteRow: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        alignItems: 'center',
        gap: 16,
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 16,
        paddingRight: 16,
        borderBottom: Sty.border,
    },
    websiteBuilderSiteRow_mobile: {
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 0,
        paddingRight: 0,
    },
    websiteBuilderSiteInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        marginRight: 'auto',
    },
    websiteBuilderSiteLinkContainerOuter: {
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
        alignItems: 'center',
    },
    websiteBuilderSiteLinkContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
        alignItems: 'center',
        borderRadius: 4,
        backgroundColor: Colors.greyLighter,
        paddingLeft: 8,
        paddingRight: 8,
        paddingTop: 4,
        paddingBottom: 4,
        wordBreak: 'break-all',
        width: 'fit-content',
        cursor: 'pointer',
    },
    websiteBuilderSiteActions: {
        display: 'flex',
        flexDirection: 'row',
        maxWidth: 360,
        justifyContent: 'space-between',
        width: '100%',
    },
    websiteBuilderSiteInfoContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
        width: '100%',
    },
    websiteBuilderSiteInfoContainer_mobile: {
        flexDirection: 'column',
    }
}

export default WebsitePage;